import type { CatalogRatioData, CatalogRatioGroupData } from '@gql/graphql';
import { DismantlingCoefficientTypeEnum, RatioTypeEnum } from '@gql/graphql';

import { useMemo } from 'react';

type TUseBaseCostRatioOutputDataProps = {
    activeTabId: RatioTypeEnum;
    responseRatioType?: RatioTypeEnum;
    selectedRatioGroup?: CatalogRatioGroupData | null;
    responseRatioGroupId?: string | number;
    ratiosData: Record<RatioTypeEnum, CatalogRatioData[] | null>;
};

export const useBaseCostRatioOutputData = ({
    activeTabId,
    responseRatioType,
    selectedRatioGroup,
    responseRatioGroupId,
    ratiosData,
}: TUseBaseCostRatioOutputDataProps) => {
    const selectedRatioGroupId = useMemo(() => selectedRatioGroup?.id, [selectedRatioGroup?.id]);

    const currentRowData = useMemo(() => {
        if (
            !responseRatioType ||
            (responseRatioType !== RatioTypeEnum.TechPartCoef && (!responseRatioGroupId || !selectedRatioGroupId))
        ) {
            return null;
        }

        return ratiosData[responseRatioType];
    }, [responseRatioType, ratiosData, responseRatioGroupId, selectedRatioGroupId]);

    const updateGroupRowData = useMemo(() => {
        if (!currentRowData || !Array.isArray(currentRowData)) {
            return [];
        }

        const hasNewObj = currentRowData.some((item) => item);

        if (hasNewObj && activeTabId !== RatioTypeEnum.TechPartCoef) {
            const rowGroup =
                selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks &&
                !selectedRatioGroup?.parentId
                    ? {
                          id: selectedRatioGroup.id,
                          name: selectedRatioGroup.name,
                          responseRatioType: currentRowData[0].ratioType,
                          groupRow: true,
                      }
                    : {
                          id: currentRowData[0].catalogRatioGroupId,
                          name: currentRowData[0].catalogRatioGroupName,
                          responseRatioType: currentRowData[0].ratioType,
                          groupRow: true,
                      };

            return [rowGroup, ...currentRowData];
        }

        return currentRowData;
    }, [
        activeTabId,
        currentRowData,
        selectedRatioGroup?.groupType,
        selectedRatioGroup?.id,
        selectedRatioGroup?.name,
        selectedRatioGroup?.parentId,
    ]);

    return {
        currentRowData,
        updateGroupRowData,
    };
};
